import React, { FC } from 'react';
import Skeleton from 'react-loading-skeleton';
import ErrorLoadingDetailProperty from 'components/shared/detail/ErrorLoadingDetailProperty';
import { HyperText } from '@kontentino/kontentino-post-previews';
import { TYPE_FACEBOOK } from '@kontentino/kontentino-constants/Pages';
import { usePostOverviewQuery } from 'app/modules/posts/modules/post/hooks/usePostOverview';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import AccordionWidgetEmptyMessage from 'app/modules/posts/components/accordionWidget/AccordionWidgetEmptyMessage';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import { Button, Chip, Icon, Popover } from '@kontentino/ui';
import TextEditorTranslationPopup from 'app/modules/textEditor/components/optionsBar/TextEditorTranslationPopup';
import useBoolean from 'utils/hooks/useBoolean';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import PostApi from 'api/post';
import { useInvalidatePostDetailQueries } from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import { AI_GENERATE_BUTTON_STYLE_CLASSNAME } from 'app/modules/aiContent/components/AIGenerateButton';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { faArrowDownArrowUp } from '@fortawesome/pro-solid-svg-icons/faArrowDownArrowUp';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';

type Props = {
  id: number;
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const InfoCardPostTranslation: FC<Props> = ({ id, accordionProps }) => {
  const { isLoading, isError, data } = usePostOverviewQuery(id);
  const [translationPopoverOpen, setTranslationPopoverOpen] = useBoolean(false);
  const invalidatePostDetail = useInvalidatePostDetailQueries();
  const savePostPartial = useMutation(PostApi.savePartial, {
    onSuccess() {
      invalidatePostDetail({
        postId: id,
      });
    },
  });
  const { t } = useTranslation();

  return (
    <AccordionWidget
      icon={<AccordionWidgetIcon variant="translation" />}
      title={<AccordionWidgetTitle variant="translation" />}
      titleSuffix={(isOpen) =>
        !isOpen && data?.translation_text.length !== 0 ? (
          <Chip className="!tw-bg-grayscale-10">
            <HyperText
              className="!tw-whitespace-nowrap !tw-text-black"
              options={{
                ellipsis: true,
                maxLength: 50,
                pageType: data?.page.type,
              }}
            >
              {data?.translation_text || ''}
            </HyperText>
          </Chip>
        ) : undefined
      }
      data-cy="post-preview_translation"
      {...accordionProps}
    >
      {isLoading && <Skeleton height={60} />}
      {isError && <ErrorLoadingDetailProperty />}
      {data?.translation_text && data.translation_text.length !== 0 && (
        <>
          <div className="tw-rounded-sm tw-border tw-border-solid tw-border-grayscale-20 tw-p-3">
            <div>
              <HyperText pageType={TYPE_FACEBOOK}>
                {data.translation_text}
              </HyperText>
            </div>
          </div>
          <UserPermissionGate scopes={UserPermission.POST_EDIT_TEXT}>
            <div className="tw-flex tw-justify-center tw-pt-2">
              <Button
                size="small"
                variant="tertiary"
                iconBefore={<Icon icon={faArrowDownArrowUp} size="sm" />}
                data-cy="post-detail-preview_swap-post-text-button"
                data-name="post-detail-preview_swap-post-text-button"
                isLoading={savePostPartial.isLoading}
                onClick={() => {
                  savePostPartial.mutate({
                    postId: id,
                    values: {
                      translationText: data.text,
                      text: data.translation_text,
                    },
                  });
                }}
              >
                {t('swapForPostText')}
              </Button>
            </div>
          </UserPermissionGate>
        </>
      )}
      {!isLoading && !isError && data?.translation_text.length === 0 && (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2">
          <AccordionWidgetEmptyMessage variant="translation" />
          {!!data.text && (
            <UserPermissionGate scopes={UserPermission.POST_EDIT_TRANSLATION}>
              <Popover.Root
                open={translationPopoverOpen}
                onOpenChange={setTranslationPopoverOpen.set}
              >
                <Popover.Trigger asChild>
                  <Button
                    variant="primary"
                    size="small"
                    isLoading={savePostPartial.isLoading}
                    data-cy="post-detail-preview_translate-post-button"
                    data-name="post-detail-preview_translate-post-button"
                    className={AI_GENERATE_BUTTON_STYLE_CLASSNAME}
                    iconBefore={<Icon icon={faSparkles} />}
                  >
                    {t('translatePostText')}
                  </Button>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    side="bottom"
                    align="center"
                    sideOffset={4}
                    className="tw-z-[1001]"
                  >
                    <TextEditorTranslationPopup
                      getTextToTranslate={() => data.text}
                      onTranslated={(translationText) => {
                        setTranslationPopoverOpen.off();
                        savePostPartial.mutate({
                          postId: id,
                          values: {
                            translationText,
                          },
                        });
                      }}
                    />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </UserPermissionGate>
          )}
        </div>
      )}
    </AccordionWidget>
  );
};

export default InfoCardPostTranslation;
